/**
 * 2026-09-23 — Duyurular → Telegram kanalı (contract C4, admin side).
 *
 *   GET  /admin/announcements                       → rows[].telegram + {telegram:{available}}
 *   POST /admin/announcements {…, postToTelegram?}  (create; server default true)
 *   PUT  /admin/announcements/:id {…, postToTelegram?}
 *   POST /admin/announcements/:id/telegram/retry    → {ok, telegram} | 404 | 409 | 503
 *
 * The server only records the intent and queues the channel work, so a create /
 * edit answers with telegram.status 'pending'; the page polls briefly until the
 * row settles. `telegram` is optional on the wire so an older API (no channel
 * support) still renders.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from './client.js';
import type { CreateAnnouncementBody, WireAdminAnnouncement } from './endpoints.js';
import { freshIdempotencyKey } from './idempotency-key.js';

export type AnnouncementTelegramStatus = 'pending' | 'posted' | 'failed' | 'deleted' | 'skipped';

export interface AnnouncementTelegramState {
  readonly enabled: boolean;
  readonly status: AnnouncementTelegramStatus;
  readonly messageId: number | null;
  readonly error: string | null;
  readonly postedAt: string | null;
}

export interface WireAdminAnnouncementWithTelegram extends WireAdminAnnouncement {
  readonly telegram?: AnnouncementTelegramState;
}

export interface AdminAnnouncementListWithTelegram {
  readonly rows: readonly WireAdminAnnouncementWithTelegram[];
  /** false/absent → the channel feature is off (TELEGRAM_ANNOUNCE_ENABLED). */
  readonly telegram?: { readonly available: boolean };
}

export interface AnnouncementBodyWithTelegram extends CreateAnnouncementBody {
  readonly postToTelegram?: boolean;
}

export const TELEGRAM_STATUS_LABEL: Readonly<Record<AnnouncementTelegramStatus, string>> = {
  posted: 'Kanalda yayında',
  pending: 'Bekliyor',
  failed: 'Hata',
  deleted: 'Silindi',
  skipped: 'Kanala gönderilmedi',
};

export function listAdminAnnouncementsWithTelegram(
  options: ApiClientOptions,
): Promise<ApiResult<AdminAnnouncementListWithTelegram>> {
  return callAdminApi(options, { method: 'GET', path: '/admin/announcements' });
}

export function createAdminAnnouncementWithTelegram(
  options: ApiClientOptions,
  input: AnnouncementBodyWithTelegram,
): Promise<ApiResult<{ announcement: WireAdminAnnouncementWithTelegram }>> {
  return callAdminApi(options, {
    method: 'POST',
    path: '/admin/announcements',
    body: input,
    idempotencyKey: freshIdempotencyKey('announcement-create'),
  });
}

export function updateAdminAnnouncementWithTelegram(
  options: ApiClientOptions,
  id: string,
  patch: Partial<AnnouncementBodyWithTelegram> & { readonly isActive?: boolean },
): Promise<ApiResult<{ announcement: WireAdminAnnouncementWithTelegram }>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/announcements/${encodeURIComponent(id)}`,
    body: patch,
    idempotencyKey: freshIdempotencyKey('announcement-update'),
  });
}

/**
 * "Tekrar dene": re-queue the channel copy of a failed / stuck row. The route
 * answers errors as `{error:'telegram_disabled'|…}`; that code is lifted into
 * `reason` so the page can show a readable message instead of `http_503`.
 */
export async function retryAnnouncementTelegram(
  options: ApiClientOptions,
  id: string,
): Promise<ApiResult<{ ok: true; telegram: AnnouncementTelegramState }>> {
  const res = await callAdminApi<{ ok: true; telegram: AnnouncementTelegramState }>(options, {
    method: 'POST',
    path: `/admin/announcements/${encodeURIComponent(id)}/telegram/retry`,
    body: {},
    idempotencyKey: freshIdempotencyKey('announcement-telegram-retry'),
  });
  if (res.kind === 'error' && typeof res.body?.error === 'string' && /^http_\d+$/.test(res.reason)) {
    return { ...res, reason: res.body.error };
  }
  return res;
}

/** Operator-facing text for the retry route's error codes. */
export function telegramRetryErrorText(reason: string): string {
  if (reason === 'telegram_disabled') return 'Telegram kanal yayını kapalı (TELEGRAM_ANNOUNCE_ENABLED).';
  if (reason === 'telegram_not_enabled') return 'Bu duyuru Telegram kanalı için işaretlenmemiş.';
  if (reason === 'not_found') return 'Duyuru bulunamadı.';
  return reason;
}
